import type { LeaderboardEntry, TransformedEpoch } from "@/components/features/campaigns/hold/HoldLeaderboard/types";
import { LEADERBOARD_CONSTANTS, EpochStatus, isTopTierRank, isMedalRank } from "@/constants/hold";
import type { HoldCampaignWithStatus } from "@/types/holdCampaigns";
import { isAfter, isWithinInterval } from "date-fns";

/**
 * Styling utilities for leaderboard components
 */

export const getRankBadgeClass = (rank: number): string => {
  const baseClasses = "w-7 h-7 flex items-center justify-center rounded-md text-xs ml-2 mr-2 font-bold";
  if (isTopTierRank(rank)) {
    return `${baseClasses} text-black`;
  }
  return `${baseClasses} bg-cede-bg-component/50 text-cede-text-grey`;
};

export const getRankBadgeStyle = (rank: number): React.CSSProperties => {
  if (rank === 1) {
    return { backgroundColor: LEADERBOARD_CONSTANTS.MEDAL_COLORS.GOLD };
  }
  if (rank === 2) {
    return { backgroundColor: LEADERBOARD_CONSTANTS.MEDAL_COLORS.SILVER };
  }
  if (rank === 3) {
    return { backgroundColor: LEADERBOARD_CONSTANTS.MEDAL_COLORS.BRONZE };
  }
  return {};
};

export const getRowClass = (entry: LeaderboardEntry): string => {
  return entry.highlight ? "border-l-2" : "";
};

export const getRowStyle = (entry: LeaderboardEntry): React.CSSProperties => {
  if (entry.highlight) {
    return {
      background: "var(--color-contrast-shade-primary-16)",
      borderLeftColor: "var(--color-cede-primary)",
    };
  }

  if (entry.rank === 1) {
    return { background: LEADERBOARD_CONSTANTS.MEDAL_GRADIENTS.GOLD };
  }
  if (entry.rank === 2) {
    return { background: LEADERBOARD_CONSTANTS.MEDAL_GRADIENTS.SILVER };
  }
  if (entry.rank === 3) {
    return { background: LEADERBOARD_CONSTANTS.MEDAL_GRADIENTS.BRONZE };
  }
  return {};
};

export const getPrizeTextClass = (entry: LeaderboardEntry): string => {
  if (entry.rewardAmount <= 0) {
    return "body2 text-cede-text-grey";
  }

  if (isTopTierRank(entry.rank)) {
    return "body2 font-semibold";
  }

  return "body2 text-cede-primary font-semibold";
};

export const getPrizeTextStyle = (entry: LeaderboardEntry): React.CSSProperties => {
  if (entry.rewardAmount > 0 && isMedalRank(entry.rank)) {
    const colorMap = {
      1: LEADERBOARD_CONSTANTS.MEDAL_COLORS.GOLD,
      2: LEADERBOARD_CONSTANTS.MEDAL_COLORS.SILVER,
      3: LEADERBOARD_CONSTANTS.MEDAL_COLORS.BRONZE,
    };
    return { color: colorMap[entry.rank] };
  }
  return {};
};

export const getTrophyColor = (rank: number): string => {
  if (!isMedalRank(rank)) return "";

  const colorMap = {
    1: LEADERBOARD_CONSTANTS.MEDAL_COLORS.GOLD,
    2: LEADERBOARD_CONSTANTS.MEDAL_COLORS.SILVER,
    3: LEADERBOARD_CONSTANTS.MEDAL_COLORS.BRONZE,
  };

  return colorMap[rank];
};

/**
 * Business logic utilities
 */

export const transformCampaignEpochs = (campaign: HoldCampaignWithStatus | null): TransformedEpoch[] => {
  if (!campaign?.epochs) return [];

  const allEpochs = campaign.epochs || [];
  const now = new Date();

  return allEpochs
    .filter((epoch) => !epoch.isFullyPaused)
    .map((epoch) => {
      const startDate = epoch.startDate;
      const endDate = epoch.endDate;

      let status: EpochStatus;
      if (isWithinInterval(now, { start: startDate ?? new Date(), end: endDate ?? new Date() })) {
        status = epoch.isPaused ? EpochStatus.PAUSED : EpochStatus.ACTIVE;
      } else if (isAfter(now, endDate ?? new Date())) {
        status = EpochStatus.COMPLETED;
      } else {
        status = EpochStatus.PENDING;
      }

      return {
        epochNumber: epoch.epochNumber ?? 0,
        activeEpochId: epoch.activeEpochId ?? 0,
        status,
        startDate: startDate ?? new Date(),
        endDate: epoch.endDate ?? new Date(),
      };
    });
};

export const findActiveEpoch = (epochs: TransformedEpoch[]): TransformedEpoch | undefined => {
  return epochs.find((epoch) => epoch.status === EpochStatus.ACTIVE || epoch.status === EpochStatus.PAUSED);
};

export const getLatestEpoch = (epochs: TransformedEpoch[]): TransformedEpoch | undefined => {
  if (epochs.length === 0) return undefined;
  return epochs.reduce((latest, current) => (current.activeEpochId > latest.activeEpochId ? current : latest));
};

export const getFirstEpoch = (epochs: TransformedEpoch[]): TransformedEpoch | undefined => {
  if (epochs.length === 0) return undefined;
  return epochs.reduce((first, current) => (current.startDate < first.startDate ? current : first));
};

export const getEpochStatusText = (status: EpochStatus): string => {
  switch (status) {
    case EpochStatus.ACTIVE:
      return "Active";
    case EpochStatus.PAUSED:
      return "Paused";
    case EpochStatus.COMPLETED:
      return "Epoch ended";
    case EpochStatus.PENDING:
      return "Epoch pending";
    default:
      return "Epoch ended";
  }
};

export const getEpochTitle = (epoch: TransformedEpoch | undefined): string => {
  if (!epoch) return "Leaderboard";

  const displayEpochNumber = epoch.activeEpochId + 1;
  return epoch.status === EpochStatus.ACTIVE || epoch.status === EpochStatus.PAUSED
    ? `Current Epoch #${displayEpochNumber}`
    : `Epoch #${displayEpochNumber}`;
};

export const getEpochDisplayNumber = (activeEpochId: number): number => {
  return activeEpochId + 1;
};

export const sortEpochsByNumber = (epochs: TransformedEpoch[]): TransformedEpoch[] => {
  return [...epochs].sort((a, b) => a.activeEpochId - b.activeEpochId);
};
